/**
 * Effectief merk voor een uitgaand antwoord.
 *
 * Gedeeld tussen backend (api/_lib/effective-brand.ts re-exporteert dit, zelfde
 * patroon als draft-gates) en frontend (ThreadDetail "Van"-selector).
 *
 * Probleem: BizziPhone-callbacks en generieke platform-leads krijgen brand
 * 'CINCO'. Cinco is een platform zonder eigen wasserij-kennis, dus een AI-draft
 * onder CINCO mist bv. de Kleda-doorlooptijd. Maar de klant heeft vaak een echt
 * KLEDA/SHS/SOFASAVER-order — dat merk is een sterker signaal dan de catch-all
 * CINCO-tag. Zo'n gesprek hoort met het órder-merk beantwoord en verstuurd te
 * worden (juiste kennis + mailbox + tone).
 *
 * Bewust conservatief: alleen de generieke CINCO-bak leent het merk van een OPEN
 * productorder. Een gesprek dat al een productmerk draagt (KLEDA/SHS/SOFASAVER)
 * wordt nooit overschreven, en een afgeronde/geannuleerde order telt niet mee
 * (voorkomt dat een oude order een echte nieuwe Cinco-aanvraag wegkaapt).
 */

/**
 * Afgeronde/geannuleerde order-statussen — spiegelt AFGEROND_STATUSES +
 * GEANNULEERD_STATUSES in api/_lib/order-status.ts. Bewust hier gedupliceerd
 * zodat deze gedeelde helper geen backend-only module hoeft te importeren; de
 * backend geeft de canonieke orderFase mee via de isOpenStatus-parameter.
 */
const CLOSED_ORDER_STATUSES = new Set<string>([
  'Geannuleerd',
  'Terugbetaald',
  'Afgerond',
  'Teruggebracht',
  'Zelf opgehaald',
  'Verzonden',
]);

/** Merk-sleutel van gesprekken uit een persoonlijke mailbox (mail_mailboxen.soort = persoonlijk). */
export const PERSOONLIJK_BRAND = 'PERSOONLIJK';

export function isOpenOrderStatusDefault(status: string | null | undefined): boolean {
  return !CLOSED_ORDER_STATUSES.has((status || '').trim());
}

export interface BrandOrderLite {
  brand?: string | null;
  status?: string | null;
}

/**
 * Retourneert het merk waaronder een antwoord op dit gesprek gemaakt/verstuurd
 * moet worden. Alleen een CINCO-gesprek met een open, niet-CINCO productorder
 * krijgt het order-merk; alle andere gevallen houden het gespreksmerk.
 *
 * `isOpenStatus` is injecteerbaar zodat de backend de canonieke orderFase kan
 * meegeven; de frontend gebruikt de meegeleverde default.
 */
export function effectiveReplyBrand(
  conversationBrand: string | null | undefined,
  orders: BrandOrderLite[] | undefined | null,
  isOpenStatus: (status: string | null | undefined) => boolean = isOpenOrderStatusDefault,
): string {
  const convBrand = (conversationBrand || 'CINCO').toUpperCase();
  // Persoonlijke mailbox (migratie 235): geen merk, de afzender is de mailbox
  // zelf; een order van de klant mag hier nooit een merk-afzender van maken.
  if (convBrand === PERSOONLIJK_BRAND) return PERSOONLIJK_BRAND;
  if (convBrand !== 'CINCO') return conversationBrand || 'CINCO';

  const productOrder = (orders || []).find((o) => {
    const b = (o.brand || '').toUpperCase();
    return b && b !== 'CINCO' && isOpenStatus(o.status);
  });
  return productOrder?.brand || conversationBrand || 'CINCO';
}
